import { FlexCol, FlexRow } from "@design-system/Layout/Flex";
import { PageContainer } from "@design-system/Layout/PageContainer";
import { Tabs } from "@design-system/Tabs";
import { useCallback, useEffect, useMemo, useState, type FC } from "react";
import { useTranslation } from "react-i18next";
import { questionnaireTabs } from "../services";

import { debounce } from "lodash";
import { useParams } from "react-router-dom";
import { useQueryParam } from "use-query-params";
import { EditQuestionnaireModal } from "../components/EditOrDeleteDropdown/EditQuestionnaireModal";
import { Loader } from "./components/Loader/Loader";
import { OverviewTab } from "./components/OverviewTab";
import { QuestionnaireHeader } from "./components/QuestionnaireHeader";
import { QuestionnaireStats } from "./components/QuestionnaireStats";
import { QuestionsTab } from "./components/QuestionsTab";
import { openInactiveQuestionsModalAtom, questionnaireParamsArgs } from "./ctx";
import {
    useQuestionnaireGenerationState,
    useQuestionnaireOverviewData,
    useQuestionnaireStats,
} from "./data";
import { useExternalQuestionnaireBreadcrumb } from "./hooks/useExternalQuestionnairesBreadcrumb";
import { useSSE, type EventType } from "./hooks/useQuestionDiscoveredSSE";
import { keys, RESOURCE_NAME } from "./i18n";
import { isQuestionGenerating } from "./services";
import { type ExternalQuestionnaireOptions } from "./types";
import { InactiveQuestionsModal } from "./components/InactiveQuestionsModal";
import { useAtom } from "jotai";

interface ExternalQuestionnaireProps {
    id: string | undefined;
    options: ExternalQuestionnaireOptions;
}

export const ExternalQuestionnaire: FC<ExternalQuestionnaireProps> = ({
    id,
    options,
}) => {
    const { t } = useTranslation(RESOURCE_NAME);

    const [activeTabParam, setActiveTabParam] = useQueryParam(
        ...questionnaireParamsArgs.tab,
    );

    const [, setGroupParam] = useQueryParam(...questionnaireParamsArgs.group);
    const activeTab = activeTabParam || questionnaireTabs.overview;
    const [openEditModal, setOpenEditModal] = useState(false);
    const [openInactiveModal, setOpenInactiveModal] = useAtom(
        openInactiveQuestionsModalAtom,
    );
    const [event, setEvent] = useState<EventType | null>(null);

    const handleEditQuestionnaireClick = () => {
        setOpenEditModal(true);
    };

    const handleActiveTabChange = (tab: string) => {
        setActiveTabParam(tab);
        setGroupParam(undefined);
    };

    const [pollingInterval, setPollingInterval] = useState<number | undefined>(
        undefined,
    );

    const {
        questions,
        isPending: isQuestionsPending,
        refetch: refetchQuestions,
    } = useQuestionnaireOverviewData(
        {
            questionnaireId: id,
            inactiveQuestions: false,
        },
        {
            pollingInterval,
        },
    );

    const { questionnaire, refetch: refetchQuestionnaire } =
        useQuestionnaireGenerationState({
            questionnaireId: id,
        });

    const { refetch: refetchStats } = useQuestionnaireStats(id, {
        pollingInterval,
    });

    const isQuestionnaireGenerating = questionnaire?.isGenerating ?? false;

    const debouncedRefetch = useCallback(
        debounce(async () => {
            refetchQuestionnaire();
            refetchQuestions();
            refetchStats();
        }, 800),
        [refetchQuestions, refetchQuestionnaire, refetchStats],
    );

    useSSE(`questionnaire-notification/${id}`, (event) => {
        setEvent(event);
        debouncedRefetch();
    });

    useEffect(() => {
        if (
            !isQuestionnaireGenerating &&
            questions?.find(isQuestionGenerating)
        ) {
            setPollingInterval(1000);
            return;
        }
        setPollingInterval(undefined);
    }, [questions]);

    const isLoading = useMemo(() => {
        return (
            isQuestionsPending ||
            (questions.length === 0 && isQuestionnaireGenerating)
        );
    }, [questions, isQuestionnaireGenerating]);

    return (
        <PageContainer>
            <FlexCol p="4" gap="6">
                <FlexCol gap="6">
                    <QuestionnaireHeader
                        questionnaireId={id}
                        isFramework={!!options.frameworkCmsId}
                        onEdit={
                            options.frameworkCmsId
                                ? options?.onEditClick
                                : handleEditQuestionnaireClick
                        }
                    />
                    <QuestionnaireStats questionnaireId={id} />
                </FlexCol>
                <FlexCol gap="4">
                    <FlexRow
                        justifyContent="between"
                        alignItems="end"
                        gap="3"
                        w="full"
                    >
                        <Tabs
                            activeTab={activeTab}
                            onActiveTabChange={handleActiveTabChange}
                            size="md"
                            className="w-full"
                        >
                            <Tabs.Trigger
                                value={questionnaireTabs.overview}
                                label={t(keys.tabs_tab_overview)}
                            />
                            <Tabs.Trigger
                                value={questionnaireTabs.questions}
                                label={t(keys.tabs_tab_questions)}
                            />
                        </Tabs>
                    </FlexRow>

                    {activeTab === questionnaireTabs.overview ? (
                        <>
                            {isQuestionnaireGenerating && (
                                <Loader title={event?.message} />
                            )}
                            <OverviewTab
                                questionnaireId={id}
                                options={options}
                                questions={questions}
                                isQuestionnaireGenerating={
                                    isQuestionnaireGenerating
                                }
                                isQuestionsPending={isLoading}
                            />
                        </>
                    ) : activeTab === questionnaireTabs.questions ? (
                        <>
                            {isQuestionnaireGenerating && (
                                <Loader title={event?.message} />
                            )}
                            <QuestionsTab
                                questionnaireId={id}
                                options={options}
                                questions={questions}
                                areQuestionsPending={isQuestionsPending}
                            />
                        </>
                    ) : null}
                </FlexCol>
            </FlexCol>
            <EditQuestionnaireModal
                questionnaireId={id}
                open={openEditModal}
                onOpenChange={setOpenEditModal}
            />
            <InactiveQuestionsModal
                questionnaireId={id}
                open={openInactiveModal}
                onOpenChange={setOpenInactiveModal}
            />
        </PageContainer>
    );
};

export const ExternalQuestionnairePage = () => {
    const { external_questionnaire_id } = useParams();
    useExternalQuestionnaireBreadcrumb(external_questionnaire_id);

    return (
        <ExternalQuestionnaire
            id={external_questionnaire_id}
            options={{
                canEditQuestions: true,
                canSetQuestionsIrrelevant: false,
                canDeleteQuestions: true,
            }}
        />
    );
};
